<base-site-layout
    [heading]="heading | translate"
    [headerIcon]="icon"
    [isDialog]="true"
    [loading]="saving()"
    (dialogCloseClicked)="cancel()"
>
    <form
        id="purchasingGroupCreateForm"
        class="row g-3"
        [formGroup]="form"
        (ngSubmit)="save()"
    >
        <div class="col purchasing-group-create-content">
            <div class="alert alert-info d-flex align-items-center gap-2 mb-3" role="alert">
                <em class="fas fa-circle-info"></em>
                <span>{{ 'Der Code kann nach dem Anlegen nicht mehr geändert werden.' | translate }}</span>
            </div>

            <div class="row g-4">
                <div class="col-12 col-lg-4">
                    <label class="form-label fw-bold required" for="purchasingGroupCode">
                        {{ 'Code' | translate }}
                    </label>
                    <input
                        id="purchasingGroupCode"
                        type="text"
                        class="form-control"
                        formControlName="code"
                        maxlength="30"
                        placeholder="{{ 'z. B. E01' | translate }}"
                        [ngClass]="{
                            'is-invalid':
                                form.controls.code.touched &&
                                (form.controls.code.hasError('required') ||
                                    form.controls.code.hasError('maxlength') ||
                                    form.controls.code.hasError('duplicateCode')),
                        }"
                    />
                    @if (form.controls.code.touched && form.controls.code.hasError('required')) {
                        <div class="invalid-feedback d-block">
                            {{ 'Code ist ein Pflichtfeld' | translate }}
                        </div>
                    }
                    @if (form.controls.code.touched && form.controls.code.hasError('maxlength')) {
                        <div class="invalid-feedback d-block">
                            {{ 'Code darf maximal 30 Zeichen lang sein' | translate }}
                        </div>
                    }
                    @if (form.controls.code.touched && form.controls.code.hasError('duplicateCode')) {
                        <div class="invalid-feedback d-block">
                            {{ 'Code ist bereits vergeben' | translate }}
                        </div>
                    }
                    <div class="form-text d-flex justify-content-end">
                        <span class="text-nowrap">{{ form.controls.code.value.length }} / 30</span>
                    </div>
                </div>

                <div class="col-12 col-lg-8">
                    <label class="form-label fw-bold required" for="purchasingGroupName">
                        {{ 'Name' | translate }}
                    </label>
                    <input
                        id="purchasingGroupName"
                        type="text"
                        class="form-control"
                        formControlName="name"
                        maxlength="100"
                        placeholder="{{ 'Name der Einkäufergruppe' | translate }}"
                        [ngClass]="{
                            'is-invalid':
                                form.controls.name.touched &&
                                (form.controls.name.hasError('required') || form.controls.name.hasError('maxlength')),
                        }"
                    />
                    @if (form.controls.name.touched && form.controls.name.hasError('required')) {
                        <div class="invalid-feedback d-block">
                            {{ 'Name ist ein Pflichtfeld' | translate }}
                        </div>
                    }
                    @if (form.controls.name.touched && form.controls.name.hasError('maxlength')) {
                        <div class="invalid-feedback d-block">
                            {{ 'Name darf maximal 100 Zeichen lang sein' | translate }}
                        </div>
                    }
                    <div class="form-text d-flex justify-content-end">
                        <span class="text-nowrap">{{ form.controls.name.value.length }} / 100</span>
                    </div>
                </div>

                <div class="col-12">
                    <label class="form-label fw-bold" for="purchasingGroupDescription">
                        {{ 'Beschreibung' | translate }} ({{ 'optional' | translate }})
                    </label>
                    <textarea
                        id="purchasingGroupDescription"
                        class="form-control"
                        formControlName="description"
                        maxlength="250"
                        rows="4"
                        placeholder="{{ 'Beschreibung der Einkäufergruppe' | translate }}"
                        [ngClass]="{
                            'is-invalid':
                                form.controls.description.touched &&
                                form.controls.description.hasError('maxlength'),
                        }"
                    ></textarea>
                    @if (form.controls.description.touched && form.controls.description.hasError('maxlength')) {
                        <div class="invalid-feedback d-block">
                            {{ 'Beschreibung darf maximal 250 Zeichen lang sein' | translate }}
                        </div>
                    }
                    <div class="form-text d-flex justify-content-end">
                        <span class="text-nowrap">{{ form.controls.description.value?.length || 0 }} / 250</span>
                    </div>
                </div>

                <div class="col-12">
                    <label class="form-label fw-bold" for="purchasingGroupCompanyCodeSelect">
                        {{ 'Zugewiesene Buchungskreise' | translate }} ({{ 'optional' | translate }})
                    </label>
                    <div class="form-text mb-2">
                        {{
                            'Nur die hier zugewiesenen Buchungskreise stehen bei der Pflege der Zuständigkeiten zur Auswahl.'
                                | translate
                        }}
                    </div>
                    @if (companyCodesLoading()) {
                        <div class="text-muted">{{ 'Buchungskreise werden geladen...' | translate }}</div>
                    } @else if (companyCodes().length === 0) {
                        <div class="text-muted">{{ 'Keine Buchungskreise verfügbar.' | translate }}</div>
                    } @else {
                        <div class="input-group">
                            <select
                                id="purchasingGroupCompanyCodeSelect"
                                class="form-select"
                                [value]="pendingCompanyCodeId()"
                                (change)="pendingCompanyCodeId.set($any($event.target).value)"
                                [disabled]="availableCompanyCodes().length === 0"
                            >
                                <option value="">
                                    @if (availableCompanyCodes().length === 0) {
                                        {{ 'Alle Buchungskreise zugewiesen' | translate }}
                                    } @else {
                                        {{ 'Bitte auswählen' | translate }}
                                    }
                                </option>
                                @for (companyCode of availableCompanyCodes(); track companyCode.id) {
                                    <option [value]="companyCode.id">
                                        {{ companyCode.code }} — {{ companyCode.name }}
                                    </option>
                                }
                            </select>
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                [disabled]="!pendingCompanyCodeId()"
                                (click)="addCompanyCode()"
                            >
                                <em class="fas fa-plus pe-1"></em>
                                {{ 'Hinzufügen' | translate }}
                            </button>
                        </div>

                        <div class="purchasing-group-selected-company-codes mt-2">
                            @if (selectedCompanyCodes().length === 0) {
                                <div class="text-muted small">
                                    {{ 'Keine Buchungskreise zugewiesen' | translate }}
                                </div>
                            } @else {
                                <ul class="list-group">
                                    @for (companyCode of selectedCompanyCodes(); track companyCode.id) {
                                        <li
                                            class="list-group-item d-flex justify-content-between align-items-center"
                                        >
                                            <span>
                                                <em class="fas fa-building text-muted me-2"></em>
                                                <strong>{{ companyCode.code }}</strong>
                                                <span class="text-muted"> — {{ companyCode.name }}</span>
                                            </span>
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-outline-danger"
                                                (click)="removeCompanyCode(companyCode.id)"
                                                [attr.aria-label]="'Entfernen' | translate"
                                                [title]="'Entfernen' | translate"
                                            >
                                                <em class="fas fa-trash"></em>
                                            </button>
                                        </li>
                                    }
                                </ul>
                            }
                            <div class="form-text mt-1">
                                {{ '{{count}} ausgewählt' | translate: { count: selectedCompanyCodeCount() } }}
                            </div>
                        </div>
                    }
                </div>
            </div>

        </div>

        <div class="col-12 d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
                <em class="fas fa-arrow-left me-1"></em>
                {{ 'Abbrechen' | translate }}
            </button>
            <button type="submit" class="btn btn-primary" [disabled]="saving() || form.invalid || form.pending">
                <em class="fas fa-check me-1"></em>
                {{ 'Speichern' | translate }}
            </button>
        </div>
    </form>
</base-site-layout>
